"use client"

import { useEffect, useRef } from "react"
import { motion, useAnimation, useInView } from "framer-motion"

// רכיב שמוסיף אנימציית הופעה בזמן גלילה
export const ScrollReveal = ({ 
  children, 
  direction = "up", // up, down, left, right
  delay = 0,
  duration = 0.5,
  distance = 50,
  once = true,
  className = "",
  style = {}
}) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once, margin: "-100px 0px" })
  const controls = useAnimation()
  
  // הגדרת כיוון האנימציה
  const getDirectionOffset = () => {
    switch (direction) {
      case "up": return { x: 0, y: distance }
      case "down": return { x: 0, y: -distance }
      case "left": return { x: distance, y: 0 }
      case "right": return { x: -distance, y: 0 }
      default: return { x: 0, y: distance }
    }
  }
  
  // הפעלת האנימציה כשהאלמנט נכנס לתצוגה
  useEffect(() => {
    if (isInView) {
      controls.start({ 
        opacity: 1, 
        x: 0, 
        y: 0, 
        transition: { 
          duration, 
          delay,
          ease: [0.25, 0.1, 0.25, 1] // cubic-bezier עם אפקט יוקרתי
        } 
      })
    }
  }, [isInView, controls, delay, duration])
  
  const offset = getDirectionOffset()
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...offset }}
      animate={controls}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  )
}

// רכיב שמוסיף אפקט פאראלקס בזמן גלילה
export const ParallaxSection = ({ 
  children, 
  speed = 0.2, 
  className = "",
  style = {}
}) => {
  const ref = useRef(null)
  const controls = useAnimation()
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const element = ref.current
      
      if (element) {
        const elementTop = element.getBoundingClientRect().top + scrollY
        const offset = (scrollY - elementTop) * speed
        
        controls.start({ 
          y: offset,
          transition: { duration: 0, ease: "linear" }
        })
      }
    }
    
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [controls, speed])
  
  return (
    <div className={`parallax-container ${className}`} ref={ref} style={{ overflow: "hidden", ...style }}>
      <motion.div
        animate={controls}
        className="parallax-content"
      >
        {children}
      </motion.div>
    </div>
  )
}

// רכיב שמוסיף אפקט הופעה הדרגתית לרשימת אלמנטים
export const StaggeredReveal = ({ 
  children, 
  staggerDelay = 0.1, 
  initialDelay = 0,
  duration = 0.5,
  direction = "up",
  distance = 50,
  once = true,
  className = "",
  style = {}
}) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once, margin: "-100px 0px" })
  
  // הגדרת כיוון האנימציה
  const getDirectionOffset = () => {
    switch (direction) {
      case "up": return { x: 0, y: distance }
      case "down": return { x: 0, y: -distance }
      case "left": return { x: distance, y: 0 }
      case "right": return { x: -distance, y: 0 }
      default: return { x: 0, y: distance }
    }
  }
  
  const offset = getDirectionOffset()
  
  const variants = {
    hidden: { opacity: 0, ...offset },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        delay: initialDelay + i * staggerDelay,
        duration,
        ease: [0.25, 0.1, 0.25, 1] // אפקט יוקרתי
      }
    })
  }
  
  return (
    <div ref={ref} className={className} style={style}>
      {Array.isArray(children) ? 
        children.map((child, i) => (
          <motion.div
            key={i}
            custom={i}
            variants={variants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {child}
          </motion.div>
        )) : 
        <motion.div
          variants={variants}
          custom={0}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {children}
        </motion.div>
      }
    </div>
  )
}

export default ScrollReveal
